import { useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook that returns a debounced version of the provided callback function
 * @param {Function} callback - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} - The debounced callback function
 */
function useDebounceCallback(callback, delay = 1000) {
  const timeoutRef = useRef(null);
  const lastArgsRef = useRef(null);
  const lastTimeRef = useRef(0);
  
  const debouncedCallback = useCallback((...args) => {
    lastArgsRef.current = args;
    
    const now = Date.now();
    const timeElapsed = now - lastTimeRef.current;
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // If enough time has elapsed, execute immediately
    if (timeElapsed >= delay) {
      lastTimeRef.current = now;
      callback(...args);
    } else {
      // Otherwise, schedule for later
      timeoutRef.current = setTimeout(() => {
        lastTimeRef.current = Date.now();
        callback(...lastArgsRef.current);
        timeoutRef.current = null;
      }, delay - timeElapsed);
    }
  }, [callback, delay]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debouncedCallback;
}

export default useDebounceCallback;
